import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  createContext,
  useContext,
} from 'react';

import 'firebase/compat/auth';
import {getAuth} from 'firebase/auth';
import {getApp} from 'firebase/app';
import {getUserDocumentRef, getUserRoleRef} from '../firebase/models';
import {useDocumentData} from 'react-firebase-hooks/firestore';
import {useCourseId} from '../hooks/router/useUrlParams';
import {
  doc,
  getDoc,
  getFirestore,
  setDoc,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  updateDoc,
  DocumentData,
} from 'firebase/firestore';

import type {
  DocumentReference,
  SetOptions,
  WriteBatch,
} from 'firebase/firestore';
import type {User as FirestoreUser} from '../firebase/models';
import type {User as AuthUser} from 'firebase/auth';
import {firebaseStringToRole} from 'contexts/profileUtil';
import {retryFailedRead} from 'utils/retryFailedRead';
import {useEngagement} from 'engagement/EngagementHook';
import {Role, RoleFirebaseString} from 'types/role';
import type {SectionDocument} from 'course/section/models/sectionDocument';
import type {CourseId} from 'types/common';
import {getAgeFromDateOfBirth} from 'course/application/firebase/models/formData';
import type {SectionId} from 'course/section/utils';
import {
  getUserAttendanceDocumentRef,
  type UserAttendance,
} from '../firebase/models/userAttendance';

/**
 * CourseUser
 * A materialized view of the logged in User in the current course. Joins
 * together properties from the Firebase Auth object with the Firestore User
 * document, and precomputes some authentication values.
 */

export type CourseUser = {
  id: string;
  /* Contains the highest role the User has in the current course */
  courseRole: Role;
  /* Contains the sections the User is related to in the current course.
   * TODO: should this be references to the sections or the full section? */
  courseSections: Array<DocumentReference<SectionDocument>>;

  /* the actual dictionary on the user document */
  sections: Record<CourseId, DocumentReference<SectionDocument>[]>;

  // some people are not 18 at the start of the course!
  isMinor: boolean;

  // portfolio projects
  portfolio: Array<string>;
  okToEmail: boolean;
  okToSendNotifications: boolean;
  /**
   * Per-user opt-out for section-reminder emails (T-24h and T-1h
   * fired by the `@cip/functions` reminder cron). Defaults to `true`
   * when absent on the user doc. Outranked downstream by the global
   * `okToEmail` flag — a user with `okToEmail === false` is filtered
   * regardless of this setting.
   *
   * Coverage caveat: the reminder cron currently iterates only
   * `['cip6']`, so this field is effective only for that course
   * today. Tracked under umbrella
   * [#2587](https://github.com/CodeinPlace/cip/issues/2587) and child
   * [#2588](https://github.com/CodeinPlace/cip/issues/2588) — once the
   * cron iterates active courses this field will gate reminders for
   * all of them.
   */
  okToSendSectionReminders: boolean;
  sentFirstEmail: boolean;

  // connections
  aboutMe: string;
  goals: string;
  wantToConnect: boolean;

  // final project groups
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  finalProjectCommunities: any;

  // i18n settings
  preferredLanguage: string;
} & Omit<FirestoreUser, 'roles' | 'sections'>;

export type SetCourseUserFunction = (
  fields: Partial<FirestoreUser>,
  options?: Partial<SetOptions>,
  batch?: WriteBatch,
) => Promise<void> | undefined;

const USER_DEFAULTS: Omit<CourseUser, 'id'> = {
  photoURL: '',
  firstName: '',
  lastName: '',
  displayName: '',
  city: '',
  country: '',
  isMinor: false,
  email: '',
  shareChatGPTConvos: false,
  shareCode: false,
  sharePeerLearn: false,
  courseRole: Role.UNREGISTERED,
  courseSections: [],
  confirmedSectionLeader: {},
  confirmedReviewer: {},
  confirmedSectionOptOut: {},
  confirmedMentor: {},
  confirmedStudent: {},
  smallGroups: {},
  sections: {},
  portfolio: [],
  okToEmail: true,
  okToSendNotifications: true,
  okToSendSectionReminders: true,
  sentFirstEmail: false,
  aboutMe: '',
  goals: '',
  wantToConnect: true,
  finalProjectCommunities: {},
  preferredLanguage: 'en',
};

const defaultData: ProfileContextData = {
  userData: undefined,
  setUserData: undefined,
  loading: true,
  error: undefined,
  sectionData: undefined,
  sectionDataIsLoading: true,
  setSectionData: undefined,
  setCachedUserData: undefined,
  userProtectedData: undefined,
  userAge: undefined,
  userAttendance: null,
  privileges: [],
};

export type ProfileContextData = {
  /**
   * Materialized view of the logged-in user of the current class. "Best-effort"
   * at showing up-to-date status. It doesn't subscribe to notifications from
   * Firestore, meaning the data may be stale if the user is operating in another
   * instance of the site, but reflects any mutations from the current session
   * via an internal cache.
   */
  userData: CourseUser | undefined;

  /**
   * The user has a collection of sub docs (possibly with different write permissions)
   * This includes things like uiTraining, protected etc. Keys are doc-ids, values
   * are the docs
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  userProtectedData: any;

  /**
   * List of privilege keys the user has access to
   */
  privileges: string[];

  /**
   * Update properties of the user on the database. Also updates the internal cache
   * if the request is successful.
   *
   * NOTE: In the long-term, we should not be managing this cache ourselves; we should
   * use something like swr-firestore (https://github.com/nandorojo/swr-firestore) to
   * manage our queries and caches. Deferring until later to avoid adding another
   * query pattern to the codebase.
   */
  setUserData: SetCourseUserFunction;
  /**
   * The current status of the initial request to load the user.
   */
  loading: boolean;

  /**
   * If the initial request to load the us
   */
  error: Error | undefined;

  /**
   * If the section data has loaded
   */
  sectionDataIsLoading: boolean;

  /**
   * Maps the user's section ids to the corresponding section documents
   */
  sectionData: Record<SectionId, SectionDocument> | undefined;

  /**
   * In case we need to update the sectionData
   */
  setSectionData: React.Dispatch<
    React.SetStateAction<Record<SectionId, SectionDocument> | undefined>
  >;

  setCachedUserData: React.Dispatch<
    React.SetStateAction<CourseUser | undefined>
  >;

  /** integer number of years old. Based on userProtectedData */
  userAge: number;

  /**
   * The attendance data for the user.
   */
  userAttendance: UserAttendance | null;
};
export const ProfileContext = createContext<ProfileContextData>(defaultData);

/** What the one-shot read of the user's course role settled to. */
interface RoleRead {
  /** The role document the read was for. */
  ref: DocumentReference<RoleFirebaseString>;
  /** The role, absent when the document does not exist or the read failed. */
  role?: RoleFirebaseString;
  /** Why the read failed, once its retries were used up. */
  error?: Error;
}

type ProfileProviderProps = {
  children?: React.ReactNode;
};
export const ProfileProvider = ({children}: ProfileProviderProps) => {
  const auth = getAuth();
  const authUser = auth.currentUser;
  const courseId = useCourseId();

  const userId = authUser?.uid ?? 'null';

  console.log('ProfileProvider: userId', userId);

  const db = getFirestore(getApp());

  // this allows for pages which can optionally be signed in to.
  // there is be a better way (which avoid the useDocumentDataOnce)
  // on a path we know will error

  const userDataRef = getUserDocumentRef(userId);
  const roleUserId = authUser?.uid ?? null;
  const userRoleRef = useMemo(
    () => getUserRoleRef(roleUserId, courseId),
    [roleUserId, courseId],
  );
  const userPrivilegesRef = doc(db, `users/${userId}/roles/privileges`);

  // this is a collection of docs associated with the user. They should all be readable
  // by the user

  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const [userData, setCachedUserData] = useState<CourseUser | undefined>(
    undefined,
  );
  const [roleRead, setRoleRead] = useState<RoleRead | null>(null);
  // The provider outlives a user or course switch, so a read for another role
  // document must not answer for this one.
  const currentRoleRead = roleRead?.ref === userRoleRef ? roleRead : null;
  const userRole = currentRoleRead?.role;
  const userRoleError = currentRoleRead?.error;
  // Loading through every retry: reporting loaded with no userData reads as
  // an unregistered user to every role gate.
  const userRoleIsLoading = Boolean(userRoleRef) && currentRoleRead === null;
  const [dbUser, dbUserIsLoading, dbUserError] = useDocumentData(userDataRef);
  const [userPrivileges, userPrivilegesIsLoading, userPrivilegesError] =
    useDocumentData(userPrivilegesRef);
  const [sectionData, setSectionData] = useState<
    Record<SectionId, SectionDocument>
  >({});
  const [sectionDataIsLoading, setSectionDataIsLoading] =
    useState<boolean>(true);
  const [userProtectedData, userProtectedLoading] = useDocumentData(
    doc(db, `users/${userId}/docs/protected`),
  );
  const [userAttendance, userAttendanceIsLoading, userAttendanceError] =
    useDocumentData(getUserAttendanceDocumentRef(userId, courseId));

  // Because we calculate derived data from the database user, we can't
  // pass the loading state down directly into the context values -
  // otherwise children will begin to render as soon as the query has
  // completed, but before the derived data has been calculated (so context.userData
  // will be undefined)
  const [userDataIsLoading, setUserDataIsLoading] =
    React.useState<boolean>(true);

  useEngagement(userDataIsLoading, userData);

  useEffect(() => {
    auth.onAuthStateChanged(user => {
      if (!user) {
        setCachedUserData(undefined);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, []);

  useEffect(() => {
    if (!userRoleRef) {
      return;
    }
    const abortController = new AbortController();
    const readRole = async () => {
      try {
        const snapshot = await retryFailedRead({
          read: () => getDoc(userRoleRef),
          signal: abortController.signal,
        });
        if (abortController.signal.aborted) {
          return;
        }
        setRoleRead({ref: userRoleRef, role: snapshot.data()});
      } catch (e) {
        if (abortController.signal.aborted) {
          return;
        }
        setRoleRead({
          ref: userRoleRef,
          error: e instanceof Error ? e : new Error(String(e)),
        });
      }
    };
    void readRole();
    return () => {
      abortController.abort();
    };
  }, [userRoleRef]);

  useEffect(() => {
    if (!dbUserIsLoading && !userRoleIsLoading && !userProtectedLoading) {
      // If the user doesn't exist
      if (dbUserError || userRoleError || !authUser?.uid) {
        // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        setUserDataIsLoading(false);
        return;
      }
      const userData = createUserData(courseId, dbUser, authUser, userRole);
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      setCachedUserData(userData);
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      setUserDataIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [
    dbUser,
    dbUserIsLoading,
    userRole,
    userRoleError,
    userRoleIsLoading,
    userProtectedLoading,
  ]);

  useEffect(() => {
    if (userData) {
      for (const section of userData.courseSections) {
        loadSingleSection(section);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [userData]);

  useEffect(() => {
    if (allSectionsLoaded()) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      setSectionDataIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [sectionData]);

  const setUserData = useCallback(
    async (
      fields: Partial<Omit<FirestoreUser, 'roles' | 'sections'>>,
      options: Partial<SetOptions> = {merge: true},
    ) => {
      await setDoc(userDataRef, fields, options);
      setCachedUserData({...userData, ...fields});
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    [userDataRef],
  );

  const loadSingleSection = async (
    sectionRef:
      | DocumentReference<SectionDocument>
      | DocumentReference<DocumentData>,
  ) => {
    if (sectionRef.path) {
      sectionRef = doc(db, sectionRef.path);
    }
    const section = await getDoc(sectionRef);
    if (section.exists()) {
      const sectionData = section.data();
      setSectionData(prev => {
        return {
          ...prev,
          [section.id]: sectionData,
        };
      });
    }
  };

  const allSectionsLoaded = () => {
    if (!userData?.courseSections) return true;
    if (!sectionData) return false;

    // for each section that the user is in...
    for (const section of userData?.courseSections) {
      const sectionPath = section.path;
      // get the last part of the path
      const sectionId = sectionPath.split('/').pop();
      // check if its been loaded
      if (!sectionData[sectionId]) return false;
    }
    return true;
  };

  const isAnythingLoading =
    userDataIsLoading || userAttendanceIsLoading || userPrivilegesIsLoading;
  const error =
    dbUserError ||
    userRoleError ||
    userAttendanceError ||
    userPrivilegesError;

  // Get list of privilege keys from the privileges document
  const privileges = userPrivileges ? Object.keys(userPrivileges) : [];

  return (
    <ProfileContext.Provider
      value={{
        userData,
        userProtectedData,
        setUserData,
        setCachedUserData,
        loading: isAnythingLoading,
        error,
        sectionData,
        sectionDataIsLoading,
        setSectionData,
        userAge: calculateUserAge(userProtectedData),
        userAttendance,
        privileges,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

/**
 * Placeholder display name assigned to a user who has no real one yet (see
 * where `computedUserData.displayName` is defaulted). Treated as "no display
 * name" by completeness checks and by the About form when deciding whether to
 * mirror the applicant's chosen name onto their profile.
 */
export const ANONYMOUS_DISPLAY_NAME = 'Anon';

export const isProfileComplete = (
  profileContext: ProfileContextData,
): boolean => {
  const userData = profileContext.userData;
  const userProtectedData = profileContext.userProtectedData;

  const hasDisplayName =
    userData.displayName &&
    userData.displayName.length > 0 &&
    userData.displayName !== ANONYMOUS_DISPLAY_NAME;
  const hasCountry = userData.country && userData.country.length > 0;
  const hasDob = userProtectedData?.dateOfBirth !== undefined;

  const hasFields = hasDisplayName && hasCountry && hasDob;
  if (!hasFields) {
    return false;
  }

  const requiredDobFields = ['day', 'month', 'year'];
  const dobFields = userProtectedData.dateOfBirth;
  const hasDobFields = requiredDobFields.every(
    field => dobFields[field] !== undefined,
  );

  return hasDobFields;
};

/**
 * Returns the current user's role in the current course.
 */
export const useUserRole = (): {role: Role; isLoading: boolean} => {
  const {userData, loading} = useContext(ProfileContext);
  return {role: userData?.courseRole ?? Role.UNREGISTERED, isLoading: loading};
};

function createUserData(
  courseId: string,
  dbUser: FirestoreUser,
  authUser: AuthUser,
  userRole: RoleFirebaseString,
): CourseUser {
  // computerUserData incrementally collects fields we compute from the underlying
  // db and auth user objects
  const computedUserData: Partial<CourseUser> = {};

  // Objects on the authUser - such as the display name, email, and
  // photo URL - are prioritized over the global defaults in USER_DEFAULTS
  // but under values that have been set in the Firestore document
  const authDisplayName = authUser?.displayName;
  if (authDisplayName) {
    const parts = authDisplayName.split(' ');
    if (parts.length >= 2) {
      const first = parts[0];
      const last = parts[parts.length - 1];
      computedUserData.displayName = authDisplayName;
      computedUserData.firstName = first;
      computedUserData.lastName = last;
    }
  }

  // Precompute authorization values for ease of client use
  const {sections, ...dbUserRest} = dbUser || {};
  // default is unregistered
  // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  let courseRole = firebaseStringToRole(userRole);

  if (dbUser?.photoURL) {
    computedUserData.photoURL = dbUser.photoURL;
  }
  if (authUser?.email) {
    computedUserData.email = authUser.email;
  }

  computedUserData.courseRole = courseRole;
  computedUserData.courseSections = sections?.[courseId] || [];
  computedUserData.displayName = dbUser?.displayName || ANONYMOUS_DISPLAY_NAME;
  computedUserData.sections = dbUser?.sections || {};
  computedUserData.portfolio = dbUser?.portfolio || [];
  computedUserData.okToEmail = dbUser?.hasOwnProperty('okToEmail')
    ? dbUser.okToEmail
    : true;
  computedUserData.okToSendNotifications = dbUser?.hasOwnProperty(
    'okToSendNotifications',
  )
    ? dbUser.okToSendNotifications
    : true;
  computedUserData.okToSendSectionReminders = dbUser?.hasOwnProperty(
    'okToSendSectionReminders',
  )
    ? dbUser.okToSendSectionReminders
    : true;
  computedUserData.sentFirstEmail = dbUser?.sentFirstEmail || false;
  computedUserData.aboutMe = dbUser?.aboutMe || '';
  computedUserData.goals = dbUser?.goals || '';
  computedUserData.wantToConnect = dbUser?.wantToConnect || true;
  computedUserData.preferredLanguage = dbUser?.preferredLanguage || '';
  // dbUserRest.displayName = computedUserData.displayName

  // The resulting computed object prioritizes values that have been manually
  // set on the Firestore user (which happens when the user updates their
  // profile) over any defaults or derived values. The one exception is roles,
  // which always uses the computed values.
  const userData: CourseUser = {
    id: authUser.uid,
    ...USER_DEFAULTS,
    ...dbUserRest,
    ...computedUserData,
  };

  return userData;
}

export function getCourseRoleNumber(courseRoleStr: RoleFirebaseString) {
  if (!courseRoleStr) return Role.UNREGISTERED;
  return firebaseStringToRole(courseRoleStr);
}


function calculateUserAge(userProtectedData) {
  if (!userProtectedData) return undefined;
  const dob = userProtectedData.dateOfBirth;
  if (!dob) return undefined;
  return getAgeFromDateOfBirth({dob});
}
